import { useTranslation } from 'react-i18next';
import { CAMPAIGN_CATEGORY_ICONS } from '@/components/campaigns/campaignCategoryIcons';
import { visibleCampaignCategories } from '@/lib/campaigns/campaignCategories';
import { cn } from '@/lib/utils';

export function CampaignCategoryBadges({ categories, muted = false, variant = 'default', compact = false, className }: {
  categories?: ReadonlyArray<string | null | undefined> | null;
  muted?: boolean;
  /** `onImage`: white on translucent black, readable over any banner. */
  variant?: 'default' | 'onImage';
  /** Short names without icons (full name on hover), for cards with little width. */
  compact?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const visible = visibleCampaignCategories(categories);
  if (visible.length === 0) return null;

  const chipClass = cn(
    'inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-semibold leading-none',
    variant === 'onImage'
      ? 'border-white/20 bg-black/60 text-white backdrop-blur-sm'
      : muted
        ? 'border-border/50 text-muted-foreground'
        : 'border-primary/30 bg-primary/5 text-primary/90',
  );

  return (
    <ul
      className={cn('flex flex-wrap gap-1', className)}
      aria-label={t('campaignCategories.label')}
      data-testid="campaign-categories"
    >
      {visible.map((key) => {
        const label = t(`campaignCategories.${key}`);
        if (compact) {
          const short = t(`campaignCategoriesShort.${key}`);
          return (
            <li
              key={key}
              className={chipClass}
              title={short === label ? undefined : label}
              data-testid={`campaign-category-${key}`}
            >
              {short}
            </li>
          );
        }
        const Icon = CAMPAIGN_CATEGORY_ICONS[key];
        return (
          <li key={key} className={chipClass}>
            <Icon className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
            {label}
          </li>
        );
      })}
    </ul>
  );
}
